/**
 * Service-worker registration + SILENT self-update.
 *
 * When a new deploy is detected the app refreshes itself — no prompt
 * (owner-requested). Timing guard: an update found at page load or on
 * a visibility resume applies immediately (nothing is in flight from
 * the user's perspective); an update that lands MID-SESSION is held
 * and applied on the next tab resume, so active labeling work is never
 * yanked out from under the user.
 */

import { activeHostMode } from "./hostMode";

// An extension host mode may declare ``noServiceWorker``: the host runs
// SW-free because a worker
// there can serve a stale shell for Django-rendered pages and, worse,
// the server answers /sw.js on that host with a self-destructing
// kill-switch that reloads every open tab once it activates. Registering
// it on every page load therefore reload-loops the whole app.
const swDisabledByHostMode = !!activeHostMode?.noServiceWorker;

let _registration = null;
let _reloading = false;
let _pendingUpdate = false;

/**
 * True while the member is signing in, or just has.
 *
 * A worker swap reloads the page. Immediately after a sign-in that is
 * ruinous: the token has been stored but the API layer has not yet
 * re-attached it to its headers, so the first call after the reload
 * comes back 401 and the app bounces the member straight back to the
 * login page they just completed. It happens on any origin whose
 * worker updates in that window, and always on a brand-new one, where
 * the login page installs the first worker and the next navigation
 * finds it waiting.
 *
 * So: never reload for a worker while on an auth route, or within
 * fifteen seconds of a token being minted. The update is not lost, it
 * is simply applied on the next visit.
 */
function inSignInWindow() {
  try {
    if (/^\/auth\//.test(window.location.pathname)) return true;
    const token = window.localStorage?.getItem("afri:jwt:access");
    if (!token) return false;
    const payload = token.split(".")[1];
    if (!payload) return false;
    const claims = JSON.parse(
      atob(
        ((b) => {
          while (b.length % 4) b += "=";
          return b;
        })(payload.replace(/-/g, "+").replace(/_/g, "/")),
      ),
    );
    const iat = typeof claims?.iat === "number" ? claims.iat : null;
    return iat != null && Math.floor(Date.now() / 1000) - iat < 15;
  } catch {
    // Storage disabled or an undecodable token: do not block the reload.
    return false;
  }
}

function reloadOnce() {
  if (_reloading) return;
  if (inSignInWindow()) {
    // Hold it for the next visit rather than interrupting a sign-in.
    _pendingUpdate = true;
    return;
  }
  // Native shells never reload for a service-worker swap: the bundle
  // is baked into the install, so the new SW serves the SAME files —
  // reloading gains nothing and, because every build stamps a fresh
  // sw.js, produced a reload on each boot / resume ("page keeps
  // reloading"). The worker still activates in the background.
  if (isNativeShell()) return;
  _reloading = true;
  window.location.reload();
}

function applyServiceWorkerUpdate() {
  // Do not even promote the waiting worker during a sign-in: a
  // controller change alone re-runs the boot path under a new worker.
  if (inSignInWindow()) {
    _pendingUpdate = true;
    return;
  }
  const waiting = _registration?.waiting;
  if (waiting) {
    waiting.postMessage({ type: "SKIP_WAITING" });
    // controllerchange normally triggers the reload; time out as a net.
    setTimeout(reloadOnce, 1500);
  } else {
    reloadOnce();
  }
}

// ``immediate``: page load / visibility resume — safe to swap now.
// Otherwise hold until the next resume.
function onUpdateDetected(immediate) {
  if (immediate && !document.hidden) {
    applyServiceWorkerUpdate();
  } else {
    _pendingUpdate = true;
  }
}

function watchRegistration(registration) {
  _registration = registration;

  // A worker may ALREADY be waiting (the deploy landed before this
  // page loaded) — we're rendering an old bundle right now; swap.
  if (registration.waiting && navigator.serviceWorker.controller) {
    onUpdateDetected(true);
  }

  registration.addEventListener("updatefound", () => {
    const incoming = registration.installing;
    if (!incoming) return;
    incoming.addEventListener("statechange", () => {
      // installed + an existing controller = an UPDATE (not first install).
      // Mid-session — hold for the next resume.
      if (incoming.state === "installed" && navigator.serviceWorker.controller) {
        onUpdateDetected(false);
      }
    });
  });
}

function registerServiceWorker(serviceWorkerFileName) {
  if ("serviceWorker" in navigator) {
    // Did a service worker already control this page when it loaded?
    // A FRESH visitor (no controller) gets one ``controllerchange`` the
    // instant the first SW claims the page — but the page is ALREADY
    // rendering the latest bundle, so reloading for that initial claim
    // gains nothing and actively harms: it hard-reloads mid-interaction
    // and wipes in-progress input. A fresh visitor part-way through the
    // two-step login had the form reset back to the email step (the
    // password field vanished ~1s in) every time. Only a genuine UPDATE
    // — swapping FROM an existing controller TO a new one — needs the
    // reload, and that path is already driven explicitly by
    // ``applyServiceWorkerUpdate``.
    const hadInitialController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker
      // ``updateViaCache: 'none'`` skips the HTTP cache when fetching
      // sw.js itself, so a SW behaviour change ships the moment the
      // bundle does — without this, iOS WKWebView holds the old SW
      // for up to 24h after a new bundle is installed.
      .register(serviceWorkerFileName, { updateViaCache: "none" })
      .then((registration) => {
        console.log("Service Worker registered with scope:", registration.scope);
        watchRegistration(registration);
        // Force an update check on every page load. ``register`` only
        // checks when the URL changes or on its 24h timer.
        try {
          const p = registration.update();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch (_) {
          /* old browser: update() not a function */
        }
      })
      .catch((error) => {
        console.log("Service Worker registration failed:", error);
      });

    // Reload exactly once when a promoted worker takes control, so the
    // fresh bundle actually renders — but NEVER for the first SW simply
    // claiming a fresh page (see ``hadInitialController`` above), which
    // would reload an already-current page and destroy in-progress
    // input like a half-entered login.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadInitialController) return;
      reloadOnce();
    });
  }
}

function awakenServiceWorker() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.active.postMessage({ type: "awaken" });
    });
  }
}

// ── Bundle drift detector ──
// SW-independent staleness check: fetch the live page shell bypassing
// every cache and compare the bundle version it references against the
// one THIS page is running. Catches wedged HTTP caches (Chrome served
// months-old main.js under the ?v=none URL bug) that no service-worker
// mechanics can see. Throttled to once per 5 minutes; runs on resume,
// so a mismatch reloads immediately.
let _lastDriftCheck = 0;

function runningBundleVersion() {
  const script = [...document.scripts].find((s) => /react-app\/main\.js/.test(s.src || ""));
  if (!script) return null;
  try {
    return new URL(script.src).searchParams.get("v");
  } catch (_) {
    return null;
  }
}

// Native shells ship the bundle INSIDE the app; the running bundle
// can't drift from "the deployed one" because there is no deployed
// one — a new bundle only arrives with a new install. On desktop the
// page shell may be served by local Django OR proxied from the cloud
// (two different ``?v=`` stamps), so this check saw "drift" on every
// window focus and reloaded the app each time you tabbed back — the
// white-flash-then-initials-then-avatar sequence. Skip it there.
function isNativeShell() {
  if (typeof window === "undefined") return false;
  // Evaluated LAZILY at each call — never cached — because on iOS the
  // Capacitor bridge can be injected after this module first runs.
  if (window.Capacitor) return true;
  // The bundled mobile SPA stamps this on <body> from index.html before
  // any script executes — a reliable early signal even pre-bridge.
  if (document.body?.classList?.contains("is-capacitor")) return true;
  const { hostname, port, protocol } = window.location;
  // Desktop shell (Tauri): local Django on 17080. Mobile bundle: the
  // WebView origin is http://localhost with NO port (Capacitor's local
  // file server) — a real web deployment never has that shape.
  if ((hostname === "127.0.0.1" || hostname === "localhost") && port === "17080") return true;
  if (hostname === "localhost" && port === "" && /^https?:$/.test(protocol)) return true;
  if (protocol === "capacitor:" || protocol === "ionic:") return true;
  return false;
}

async function checkBundleDrift() {
  if (isNativeShell()) return;
  const now = Date.now();
  if (now - _lastDriftCheck < 5 * 60 * 1000) return;
  _lastDriftCheck = now;
  const mine = runningBundleVersion();
  if (!mine || mine === "none") return;
  try {
    const res = await fetch(window.location.pathname, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    });
    if (!res.ok) return;
    const html = await res.text();
    const match = html.match(/react-app\/main\.js\?v=([\w.-]+)/);
    if (match && match[1] && match[1] !== mine) {
      // ONE automatic reload per deployed version. If we already
      // reloaded for this version and still run the old bundle
      // (an intermediary cache keeps serving stale HTML), reloading
      // again would loop forever — an annotator reported the app
      // "constantly reloading". Let the session continue on the old
      // bundle instead; the next real navigation picks up the deploy.
      const key = `afri:drift-reloaded:${match[1]}`;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, "1");
      } catch (_) {
        /* storage unavailable — reload unguarded */
      }
      reloadOnce();
    }
  } catch (_) {
    /* offline — try again next resume */
  }
}

// sw.js and sw-fallback.js are part of the label-studio/label_studio/core/static/js/ directory
// and are copied with the rest of the static files when running `python manage.py collectstatic`
if (swDisabledByHostMode) {
  // Only a worker LEFT OVER from an earlier visit gets the kill-switch
  // (its activation unregisters it and reloads the tab exactly once);
  // a clean visitor never registers anything, so nothing can loop.
  if ("serviceWorker" in navigator && navigator.serviceWorker.getRegistrations) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => {
        if (regs.length) {
          navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).catch(() => {});
        } else if (typeof caches !== "undefined" && caches.keys) {
          caches
            .keys()
            .then((keys) => keys.forEach((k) => caches.delete(k)))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }
} else {
  registerServiceWorker("/sw.js");
}

// On visibility resume: wake the SW for cache cleanup, apply any update
// that arrived while the tab was backgrounded, re-check for new ones,
// and verify the running bundle matches the deployed one. This is how a
// phone tab that sat for days silently catches up.
document.addEventListener("visibilitychange", () => {
  if (swDisabledByHostMode) return;
  if (!document.hidden) {
    awakenServiceWorker();
    // Native shells: the SW and bundle only change together, on
    // reinstall — never mid-session. Don't run the update/reload
    // machinery on every focus; the boot-time registration path
    // already adopts a new SW after an install.
    if (isNativeShell()) return;
    if (_pendingUpdate) {
      _pendingUpdate = false;
      applyServiceWorkerUpdate();
      return;
    }
    if (_registration) {
      try {
        const p = _registration.update();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (_) {
        /* ignore */
      }
      if (_registration.waiting && navigator.serviceWorker.controller) {
        onUpdateDetected(true);
        return;
      }
    }
    checkBundleDrift();
  }
});
